import React from "react";
import { createContext, useState, useMemo, useContext } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ThreeScene } from "./ThreeScene";
import ToolRenderer from "./ToolRenderer";
import { DesignShortcuts } from "./DesignShortcuts";
import styles from './StepPage.module.css'
import { CursorManager } from "./CursorManager";
import ShortcutsHelp from "./ShortcutsHelp";
import SaveDraftDialog from "./drafts/SaveDraftDialog";
import LoadDraftDialog from "./drafts/LoadDraftDialog";
import WorkerLoadingIndicator from "./WorkerLoadingIndicator";
import LoadingOverlay from "./LoadingOverlay";
import useEditorStore from "../stores/useEditorStore";
import { useExport } from "../hooks/useExport";

// 1. Create a context to hold our layout setters
const LayoutContext = createContext(null);

// 2. Create the main layout component
const DesignLayout = () => {
  const location = useLocation();
  const isFinalizationStep = location.pathname === '/design/main/finalization';
  const isScanFinalPreview = location.pathname === '/design/scan/final-preview';

  // Loading states
  const isLoading = useEditorStore(state => state.isLoading);
  const loadingMessage = useEditorStore(state => state.loadingMessage);

  // Export functionality
  const { handleExport, isExporting, error: exportError } = useExport();

  // State for each "slot" in our UI
  const [headerContent, setHeaderContent] = useState(null);
  const [toolbarContent, setToolbarContent] = useState(null);
  const [infoPanelContent, setInfoPanelContent] = useState(null);
  const [footerContent, setFooterContent] = useState(null);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    setHeader: setHeaderContent,
    setToolbar: setToolbarContent,
    setInfoPanel: setInfoPanelContent,
    setFooter: setFooterContent,
  }), []);

  return (
    <LayoutContext.Provider value={contextValue}>
      <div className={isFinalizationStep || isScanFinalPreview ? styles.finalizationLayout : styles.stepPage}>
        {/* Header */}
        {headerContent}

        <div className={isFinalizationStep || isScanFinalPreview ? styles.finalizationMainContent : styles.mainContent}>
          {/* Left side - Toolbar OR Finalization/Scan Final Preview Details */}
          {isFinalizationStep || isScanFinalPreview ? (
            <div className={styles.finalizationLeftPanel}>
              <Outlet />
            </div>
          ) : (
            <div className={styles.toolbar}>
              {toolbarContent}
            </div>
          )}

          {/* Center - Always the 3D Scene (persistent) */}
          <div className={isFinalizationStep || isScanFinalPreview ? styles.finalizationRightPanel : styles.centerContent}>
            <main className={isFinalizationStep || isScanFinalPreview ? styles.finalizationCanvasContainer : styles.workspace}>
              <div className={styles.sceneContainer}>
                <ThreeScene>
                  <ToolRenderer />
                  {/* Only show design shortcuts for standard steps, not for finalization or scan final preview */}
                  {!isFinalizationStep && !isScanFinalPreview && <DesignShortcuts/>}
                  <CursorManager/>
                  <ShortcutsHelp />
                  {/* The Outlet renders inside for standard layout, but is moved to left panel for finalization and scan final preview */}
                  {!isFinalizationStep && !isScanFinalPreview && <Outlet />}
                </ThreeScene>
                {/* Worker loading indicator - shows at bottom center during worker operations */}
                <WorkerLoadingIndicator />
                {/* Loading overlay - shows during apply operations */}
                {isLoading && <LoadingOverlay message={loadingMessage} />}
              </div>
            </main>

          </div>
          
          {/* Right Info Panel - Only for standard layout */}
          {!isFinalizationStep && !isScanFinalPreview && (
            <div className={styles.infoPanel}>
              {infoPanelContent}
            </div>
          )}
        </div>

        {/* Footer */}

            {/* Export section for finalization step */}
            {isFinalizationStep && (
              <section className={styles.finalizationExportSection} aria-label="Finalization actions">
                <article className={styles.finalizationExportCard}>
                  <div className={styles.cardContent}>
                    <h3>Export</h3>
                    <p>Export your scan to use it somewhere else</p>
                  </div>
                  <button
                    className={styles.finalizationExportButton}
                    onClick={handleExport}
                    disabled={isExporting}
                    aria-label="Export the scan"
                    title={exportError || "Export your socket model"}
                  >
                    {isExporting ? 'Exporting...' : 'Download Socket'}
                  </button>
                </article>

                <article className={styles.finalizationStatusCard}>
                  <div className={styles.cardContent}>
                    <h3>Your Socket is ready</h3>
                    <p>Check your socket and order now!</p>
                  </div>
                  <button className={styles.finalizationOrderButton} aria-label="Proceed to order">
                    Order Now
                  </button>
                </article>
              </section>
            )}

            {/* Error message for export */}
            {exportError && isFinalizationStep && (
              <div className={styles.exportErrorMessage} role="alert">
                {exportError}
              </div>
            )}
        
        {/* Footer - Only for standard layout */}
        {!isFinalizationStep && (
          <div className={styles.footer}>
            {footerContent}
          </div>
        )}

        {/* Draft Management Dialogs */}
        <SaveDraftDialog />
        <LoadDraftDialog />
      </div>
    </LayoutContext.Provider>
  );
};

// 3. Create a custom hook to easily access the slot setters
export const useLayoutSlots = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    // Return a no-op context to prevent errors during initial render
    return {
      setHeader: () => {},
      setToolbar: () => {},
      setInfoPanel: () => {},
      setFooter: () => {},
    };
  }
  return context;
};

export default DesignLayout;